import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

/**
 * Theme options
 */
export type ThemeOption = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/**
 * Theme context value
 */
export interface ThemeContextValue {
  theme: ThemeOption;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemeOption) => void;
  systemTheme: ResolvedTheme;
}

const THEME_KEY = 'backtime_theme';

/**
 * ThemeContext - Centralized theme management for the application
 *
 * Provides theme state with three options: 'light', 'dark', 'system'
 * Persists preference to localStorage and applies appropriate class to document
 */
const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Get the system's preferred color scheme
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark'; // Default to dark if can't detect
}

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider - Wraps the app and provides theme management
 */
export function ThemeProvider({ children }: ThemeProviderProps): React.ReactElement {
  // Initialize theme from localStorage or default to 'system'
  const [theme, setThemeState] = useState<ThemeOption>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved && ['light', 'dark', 'system'].includes(saved)) {
        return saved as ThemeOption;
      }
    }
    return 'system';
  });

  // The actual resolved theme (always 'light' or 'dark')
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (theme === 'system') {
      return getSystemTheme();
    }
    return theme as ResolvedTheme;
  });

  // Update resolved theme when theme changes or system preference changes
  useEffect(() => {
    const updateResolvedTheme = () => {
      if (theme === 'system') {
        setResolvedTheme(getSystemTheme());
      } else {
        setResolvedTheme(theme as ResolvedTheme);
      }
    };

    updateResolvedTheme();

    // Listen for system theme changes
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => updateResolvedTheme();

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
      }
      // Legacy browsers
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [theme]);

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;

    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedTheme]);

  // Set theme and persist to localStorage
  const setTheme = useCallback((newTheme: ThemeOption): void => {
    if (['light', 'dark', 'system'].includes(newTheme)) {
      setThemeState(newTheme);
      localStorage.setItem(THEME_KEY, newTheme);
    }
  }, []);

  const value: ThemeContextValue = {
    theme,
    resolvedTheme,
    setTheme,
    systemTheme: getSystemTheme(),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme - Hook to access the theme context
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
